import {ActionIcon, Button, Input, List, Modal, TextInput} from "@mantine/core";
import React, {useEffect, useState} from "react";
import {Attachment, Exercise, ExerciseAttachments, Stage, UploadAttachment} from "../../../../../types/domain";
import {ModalType} from "../../../../../types/UITypes";
import {getFileNameFromPath, getTotalAttachmentSize} from "../helperFunctions";
import {MAX_UPLOAD_SIZE_BYTES} from "../../../../../types/constants";
import {IconTrash} from "@tabler/icons-react";
import {UploadModal} from "./UploadModal";
import CustomModal from "./CustomModal";

type UpdateExerciseModalProps = {
    opened: boolean,
    close: () => void,
    exercise: Exercise | null,
    onUpdate: (updated: { name: string, description: string, material: string }) => void,
    title?: string,
    newAttachments: Array<ExerciseAttachments>,
    updateFields: (fields: { newAttachments: Array<ExerciseAttachments> }) => void,
    allStages: Array<Stage>,
    planStages: Array<Stage>,
    selectedAttachments: Array<Attachment>
};

export function UpdateExerciseModal({opened, close,
                                        exercise, onUpdate,
                                        title, newAttachments, updateFields,
                                        allStages, planStages, selectedAttachments
                                    }: UpdateExerciseModalProps) {

    const [nameState, setNameState] = useState(exercise?.name ?? "");
    const [descriptionState, setDescriptionState] = useState(exercise?.description ?? "");
    const [materialState, setMaterialState] = useState(exercise?.material ?? "");
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [localAttachments, setLocalAttachments] = useState<Array<UploadAttachment>>([]);
    const [modal, setModal] = useState<ModalType>({
        show: false,
        title: "",
        body: "",
        decline: false,
        handleOk: () => setModal(m => ({...m, show: false})),
        handleCancel: () => setModal(m => ({...m, show: false}))
    });

    useEffect(() => {
        if (opened) {
            setNameState(exercise?.name ?? "");
            setDescriptionState(exercise?.description ?? "");
            setMaterialState(exercise?.material ?? "");

            // Load any already-queued attachments for this exercise
            const existing = newAttachments.find(a =>
                a.exercise.name === exercise?.name &&
                a.exercise.description === exercise?.description &&
                a.exercise.material === exercise?.material
            );
            setLocalAttachments(existing?.attachments ?? []);
        }
    }, [opened, exercise?.name, exercise?.description, exercise?.material]);

    // Sync localAttachments back into newAttachments whenever they change
    useEffect(() => {
        if (!opened) return;

        const updated = [...newAttachments];
        const existingIndex = updated.findIndex(a =>
            a.exercise.name === exercise?.name &&
            a.exercise.description === exercise?.description &&
            a.exercise.material === exercise?.material
        );

        if (localAttachments.length === 0) {
            // Remove the entry entirely if no attachments remain
            if (existingIndex !== -1) {
                updated.splice(existingIndex, 1);
            }
        } else {
            if (existingIndex !== -1) {
                updated[existingIndex] = {...updated[existingIndex], attachments: localAttachments};
            } else {
                // Find the matching exercise object from existing entries or fall back to a stub
                const exerciseRef = newAttachments.find(a =>
                    a.exercise.name === exercise?.name &&
                    a.exercise.description === exercise?.description &&
                    a.exercise.material === exercise?.material
                )?.exercise ?? exercise!;
                updated.push({exercise: exerciseRef, attachments: localAttachments});
            }
        }

        updateFields({newAttachments: updated});
    }, [localAttachments]);

    /**
     * Delete upload and not saved attachment from exercise
     * @param a attachment to delete
     */
    function deleteAttachment(a: UploadAttachment) {
        setLocalAttachments(prev =>
            prev.filter(ua => ua.type.name !== a.type.name || ua.name !== a.name)
        );
    }

    /**
     * Handle open of upload modal
     */
    function handleOpenUploadModal() {
        const currentSize = getTotalAttachmentSize(newAttachments);
        if (currentSize >= MAX_UPLOAD_SIZE_BYTES) {
            setModal((m: any) => ({
                ...m,
                show: true,
                title: "Upload-Limit erreicht",
                body: "Die maximale Upload-Größe von 20MB wurde erreicht. Bitte speichern Sie den Plan zuerst, bevor Sie weitere Anhänge hinzufügen.",
            }));
            return;
        }
        setShowUploadModal(true);
    }

    /**
     * Handle final exercise update
      */
    const handleUpdate = () => {
        onUpdate({
            name: nameState,
            description: descriptionState,
            material: materialState
        });
        close();
    };

    return (
        <>
            <Modal opened={opened} onClose={close}
                   centered title={title}
                   size="lg">
                <Input.Wrapper>
                    <TextInput
                        label="Übung / Tätigkeit"
                        mb={"sm"}
                        value={nameState}
                        onChange={(e) => setNameState(e.currentTarget.value)}/>
                    <TextInput
                        label="Beschreibung"
                        mb={"sm"}
                        value={descriptionState}
                        onChange={(e) => setDescriptionState(e.currentTarget.value)}/>
                    <TextInput
                        label="Material"
                        mb={"sm"}
                        value={materialState}
                        onChange={(e) => setMaterialState(e.currentTarget.value)}/>
                </Input.Wrapper>

                <Button onClick={handleOpenUploadModal} mb={"sm"}>
                    Anhang hinzufügen
                </Button>

                {/* Already saved attachments */}
                {selectedAttachments.length > 0 &&
                    <div style={{marginBottom: "1rem"}}>
                        <h6>Ausgewählte Anhänge</h6>
                        <List>
                            {selectedAttachments
                                .filter(a => a.attachmentType.name !== 'Lösungsblatt')
                                .flatMap((a, index) => (
                                    <List.Item key={index}>
                                        <span style={{color: "gray"}}>
                                            {a.attachmentType.name}: {getFileNameFromPath(a.path)}
                                            {a.solutionPath && a.solutionPath !== '' &&
                                                <> | Lösung: {getFileNameFromPath(a.solutionPath)}</>
                                            }
                                        </span>
                                    </List.Item>
                                ))
                            }
                        </List>
                    </div>
                }

                {/* Newly attachments */}
                {localAttachments.length > 0 &&
                    <div style={{marginBottom: "1rem"}}>
                        <h6>Hinzugefügte Anhänge</h6>
                        <List>
                            {localAttachments.map((a, index) =>
                                <List.Item key={index}>
                                    <div style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                                        {a.type?.name}: {a.name}
                                        {a.solutionFile && ` | Lösungsblatt: ${a.solutionFile.name}`}
                                        <ActionIcon color="red" onClick={() => deleteAttachment(a)}>
                                            <IconTrash/>
                                        </ActionIcon>
                                    </div>
                                </List.Item>
                            )}
                        </List>
                    </div>
                }
                <hr/>
                <Button onClick={handleUpdate} mb={"sm"}>
                    Aktualisieren
                </Button>
            </Modal>
            <UploadModal
                opened={showUploadModal}
                close={() => setShowUploadModal(false)}
                attachments={localAttachments}
                onAdd={(attachment) => setLocalAttachments(prev => [...prev, attachment])}
                allStages={allStages}
                planStages={planStages}/>
            <CustomModal
                title={modal.title}
                body={modal.body}
                decline={false}
                showModal={modal.show}
                handleOk={modal.handleOk}
                handleCancel={modal.handleCancel}/>
        </>
    )
}